/**
 * Single source of truth for the settling-in lifecycle status vocabulary.
 *
 * Each stage carries a discriminant, a `LucideIcon` (the non-colour channel), a
 * Lingui `MessageDescriptor` label (rendered `i18n._(label)`, never `.replace()`),
 * an `isQuiet` flag, and the `StatusBadge` tone it renders with.
 *
 * Both the list lozenge (SettlingIn.tsx) and the detail stepper
 * (components/settling-in/LifecycleStepper.tsx) render from here, so the two
 * surfaces cannot drift.
 *
 * Where a stage comes from differs by surface, and that is deliberate:
 *   - the LIST renders the server's `derived_status` (the single authority);
 *   - the DETAIL stepper calls `deriveSettlingInStage`, which sees only the four
 *     stages derivable from nested entries it has already loaded.
 * One authority per surface — the two never both derive the same row.
 *
 * Colour policy: the lozenge is a `StatusBadge`, so its colours are the shared
 * status tones (gated for contrast in every theme). Two stages may share a tone;
 * the icon and the label always tell them apart (WCAG 1.4.1).
 */

import { AlertTriangle, BadgeCheck, CalendarClock, CheckCircle2, PlayCircle, Receipt } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { msg } from '@lingui/macro';
import type { MessageDescriptor } from '@lingui/core';
import { differenceInCalendarDays } from 'date-fns';
import { parseDateString } from '@/utils/dateUtils';
import type { SettlingInPlan } from '@/types/settlingIn';
import type { StatusTone } from '@/components/ui/status-badge';

/**
 * The six lifecycle stages of the spec §2.1 vocabulary.
 *
 * These come from the server: the list endpoint's `derived_status` is the single
 * authority, computed in SQL so that the `status` filter and `sort=needs_action`
 * can be applied before LIMIT/OFFSET.
 *
 * `needs_attention` and `billed` must NEVER be inferred client-side. They depend on
 * `missing_actual_count` and `is_billed`, which only the server can see; a client-side
 * guess would silently disagree with the server's filter and sort. Render
 * `plan.derived_status` — do not reach for `deriveSettlingInStage` on the list.
 */
export type SettlingInStage =
  | 'planned'
  | 'running'
  | 'needs_attention'
  | 'completed'
  | 'signed_off'
  | 'billed';

export interface SettlingInStatusStyle {
  /** The resolved stage, exposed for callers that also drive their own layout. */
  stage: SettlingInStage;
  /** The `StatusBadge` tone of the lozenge. */
  tone: StatusTone;
  /** Leading status glyph — the non-colour channel (WCAG 1.4.1). */
  icon: LucideIcon;
  /** Short status label; consumers render `i18n._(label)`. */
  label: MessageDescriptor;
  /** `true` when the stage carries no accent, so a quiet list stays quiet. */
  isQuiet: boolean;
}

/** The plan fields the derivation reads — everything is on the list response. */
export type StageDerivablePlan = Pick<
  SettlingInPlan,
  'start_date' | 'settling_in_completed' | 'sign_off_date'
>;

/**
 * Resolve a plan to its lifecycle stage, evaluated in this exact precedence order:
 * signed_off > completed > running > planned.
 *
 * ⚠ DETAIL-PAGE ONLY (`components/settling-in/LifecycleStepper.tsx`), where no enriched
 * list field exists and the nested entries are already loaded. It honestly derives only
 * the four stages it can see — it can never return `needs_attention` or `billed`, which
 * depend on server-only facts.
 *
 * The LIST must NOT call this: it renders the server's `derived_status`, the single
 * authority. Two derivations that can disagree is worse than either alone.
 *
 * Compares calendar dates, not timestamps, so a plan starting today reads as
 * `running` regardless of the clock. `today` is injectable solely so tests are
 * deterministic; production callers omit it.
 */
export function deriveSettlingInStage(plan: StageDerivablePlan, today: Date = new Date()): SettlingInStage {
  if (plan.sign_off_date) return 'signed_off';
  if (plan.settling_in_completed === true) return 'completed';

  const start = parseDateString(plan.start_date);
  if (start && differenceInCalendarDays(start, today) <= 0) return 'running';

  return 'planned';
}

/**
 * The label for the "Billed" stage — shared by the list lozenge and the stepper's
 * terminal node (spec §2.3), so the two surfaces cannot drift and there is exactly
 * one "Billed" msgid. Declared before STYLES because STYLES references it.
 */
export const BILLED_LABEL: MessageDescriptor = msg({
  id: 'settlingIn.stage.billed',
  message: 'Billed',
});

const STYLES: Record<SettlingInStage, SettlingInStatusStyle> = {
  planned: {
    stage: 'planned',
    tone: 'neutral',
    icon: CalendarClock,
    label: msg({ id: 'settlingIn.stage.planned', message: 'Planned' }),
    isQuiet: true,
  },
  running: {
    stage: 'running',
    tone: 'accent',
    icon: PlayCircle,
    label: msg({ id: 'settlingIn.stage.running', message: 'Running' }),
    isQuiet: false,
  },
  needs_attention: {
    stage: 'needs_attention',
    tone: 'warning',
    icon: AlertTriangle,
    label: msg({ id: 'settlingIn.stage.needsAttention', message: 'Needs action' }),
    isQuiet: false,
  },
  completed: {
    stage: 'completed',
    // Done but not yet signed off: one step left, so it informs rather than celebrates.
    tone: 'info',
    icon: CheckCircle2,
    label: msg({ id: 'settlingIn.stage.completed', message: 'Completed' }),
    isQuiet: false,
  },
  signed_off: {
    stage: 'signed_off',
    tone: 'success',
    icon: BadgeCheck,
    label: msg({ id: 'settlingIn.stage.signedOff', message: 'Signed off' }),
    isQuiet: false,
  },
  billed: {
    stage: 'billed',
    // Shares the success tone with signed_off; the receipt icon and the label set it apart.
    // Reuses the stepper's existing descriptor — one "Billed" msgid, not two.
    tone: 'success',
    icon: Receipt,
    label: BILLED_LABEL,
    isQuiet: false,
  },
};

/** Resolve a stage to its tone, icon and label. */
export function getSettlingInStatus(stage: SettlingInStage): SettlingInStatusStyle {
  return STYLES[stage];
}

/**
 * Is this a stage we can render?
 *
 * `derived_status` arrives as an optional string on the generated type, and the
 * shared `PlanResponse` carries it empty on non-list flows (the detail endpoint does
 * not compute it). Rendering a lozenge for an unknown value would crash the row on
 * `undefined.icon`, so callers gate on this instead of trusting the string.
 *
 * This is a render guard, NOT a derivation: it never invents a stage for a plan that
 * has none — the server stays the single authority.
 */
export function isSettlingInStage(value: string | undefined | null): value is SettlingInStage {
  return value != null && value in STYLES;
}
